import { FC, useEffect, useState } from 'react';
import { Modal, Select, Button as AntButton } from 'antd';
import {
  ISemesterCoursesSubject,
  ISemesterCourseSubjectData,
} from '../../../../models/semesterCoursesSubject.model';
import Checkbox from '../../../Checkbox';
import useAxios from '../../../../hooks/useAxios';
import { IGetSubjects } from '../../../../models/subject.model';
import { getSubjects } from '../../../../api/subject.api';
import Loader from '../../../Loader';
import ErrorResponse from '../../../ErrorResponse';
import useNotification from '../../../../hooks/useNotification';
import Button from '../../../Button';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

type Status = 'Create' | 'Edit' | 'Read';

interface IProps {
  semesterCoursesSubject: ISemesterCoursesSubject[];
  level: number;
  semesterCourseCode: number;
  onClose: () => void;
  onOk: (
    body: ISemesterCourseSubjectData[],
    status: 'Create' | 'Update',
  ) => void;
}

const EditSubjectModal: FC<IProps> = ({
  level,
  semesterCourseCode,
  onClose,
  onOk,
  semesterCoursesSubject,
}) => {
  const { t } = useTranslation();
  const { infoNotification } = useNotification();

  const {
    data: res,
    isLoading: isLoading,
    error: error,
    execute: execute,
  } = useAxios<IGetSubjects>();

  const [data, setData] = useState<ISemesterCourseSubjectData[]>(
    Array.from({ length: 4 }, () => ({
      checked: false,
      semesterCourseCode,
      subjectCode: 0,
      totalLessonCount: 0,
      weekLessonCount: 0,
      status: 'create',
    })),
  );
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [status, setStatus] = useState<Status>('Create');

  const onSelectAll = () => {
    setData(data.map((item) => ({ ...item, checked: !isSelectAll })));
    setIsSelectAll(!isSelectAll);
  };

  const onSelect = (index: number) => {
    const newData = [...data];
    newData[index].checked = !data[index].checked;
    setData(newData);
  };

  const onSubjectCodeChange = (subjectCode: number, index: number) => {
    const newData = [...data];
    newData[index].subjectCode = subjectCode;
    setData(newData);
  };

  const onWeekLessonCountChange = (weekLessonCount: number, index: number) => {
    const newData = [...data];
    newData[index].weekLessonCount = weekLessonCount;
    setData(newData);
  };

  const getTotalLessonCount = (weekLessonCount: number) => {
    return weekLessonCount * 12;
  };

  const onAdd = () => {
    const newData = [...data];
    newData.push({
      checked: false,
      semesterCourseCode,
      subjectCode: 0,
      totalLessonCount: 0,
      weekLessonCount: 0,
      status: 'create',
    });
    setData(newData);
  };

  const onRemove = () => {
    const checks = data.filter(({ checked }) => checked);
    if (checks.length === 0) {
      return infoNotification(t('program.semester.14'));
    }
    if (semesterCoursesSubject.length > 0) {
      setData(
        data.map((item) => {
          if (item.checked) {
            item.checked = false;
            item.status = 'delete';
          }
          return item;
        }),
      );
    } else {
      setData(data.filter(({ checked }) => !checked));
    }
  };

  const onLocalClick = () => {
    switch (status) {
      case 'Create':
        onOk(data, 'Create');
        break;
      case 'Edit':
        onOk(data, 'Update');
        break;
      case 'Read':
        setStatus('Edit');
        break;
    }
  };

  useEffect(() => {
    execute(getSubjects());
  }, []);

  useEffect(() => {
    if (semesterCoursesSubject.length > 0) {
      setStatus('Read');
      setData(
        semesterCoursesSubject.map((item) => ({
          ...item,
          checked: false,
          semesterCourseCode,
          status: 'update',
        })),
      );
    }
  }, [semesterCoursesSubject]);

  return (
    <>
      {isLoading && <Loader />}
      <ErrorResponse error={error} />
      <Modal
        title={t('program.semester.15')}
        visible
        onCancel={onClose}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <Button onClick={onLocalClick}
                    className='btn-basic pink'
              text={status === 'Read' ? t('common.button.edit') : t('common.button.confirm')}
            />
          </div>
        ]}
      >
        <div className="comm-bx__space">
          <h3 className='sub-title'>{level}</h3>
        </div>
        <div className="comm-table__area rowType stats mt-0">
          <table>
            <colgroup>
              <col width="80px" />
              <col width="*" />
              <col width="*" />
              <col width="*" />
            </colgroup>
            <tbody>
              <tr>
                <th>
                  <label className="check-area justify-content-center">
                  <Checkbox
                    checked={isSelectAll}
                    onChange={onSelectAll}
                    text=""
                    
                    disabled={status === 'Read'}
                  />
                  </label>
                </th>
                <th>{t('program.semester.16')}</th>
                <th>{t('program.semester.17')}</th>
                <th>{t('program.semester.18')}</th>
              </tr>
              {data.map(
                (
                  { checked, subjectCode, weekLessonCount, status: mapStatus },
                  index,
                ) => {
                  if (mapStatus !== 'delete')
                    return (
                      <tr key={index}>
                        <td>
                          <Checkbox
                            checked={checked}
                            onChange={() => onSelect(index)}
                            text=""
                            disabled={status === 'Read'}
                          />
                        </td>
                        <td>
                          <Select
                            className="w-100"
                            onChange={(value) =>
                              onSubjectCodeChange(value, index)
                            }
                            value={subjectCode === 0 ? undefined : subjectCode}
                            disabled={status === 'Read'}
                          >
                            {res?.data.map(
                              ({ subjectCode, subjectName }, index2) => (
                                <Option key={index2} value={subjectCode}>
                                  {subjectName}
                                </Option>
                              ),
                            )}
                          </Select>
                        </td>
                        <td>
                          <Select
                            onChange={(value) =>
                              onWeekLessonCountChange(value, index)
                            }
                            value={
                              weekLessonCount === 0
                                ? undefined
                                : weekLessonCount
                            }
                            disabled={status === 'Read'}
                          >
                            {Array.from({ length: 6 }).map((item, index) => (
                              <Option key={index} value={index + 1}>
                                {index + 1}
                              </Option>
                            ))}
                          </Select>
                        </td>
                        <td>{getTotalLessonCount(weekLessonCount)}</td>
                      </tr>
                    );
                },
              )}
            </tbody>
          </table>
          {status !== 'Read' && (
            <div className="comm-bx__space">
              <div className="comm-btn__area right">
                <Button
                  className="btn-basic small gray"
                  text={t('common.button.delete')}
                  onClick={onRemove}
                />
                <Button
                  className="btn-basic small pink-line"
                  text={t('common.button.add')}
                  onClick={onAdd}
                />
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default EditSubjectModal;
